import { useState } from 'react';
import { receptionApi, type ChangeSetView } from '../api/client';
import { useData } from '../lib/useData';
import { ReviewDrawer } from './ReviewDrawer';
import { Card, Notice } from './ui';

/**
 * A person's seat (1.13.0, docs/receptionist.md): Standard or Receptionist. Making someone a
 * receptionist is a change set (their extension takes queue calls, and Operators follows), shown
 * before it applies; the seat follows once it's applied. Only for people who sign in to Stitch.
 */
export function SeatCard({ domain, extension, name, writable, onChanged }: { domain: string; extension: string; name: string; writable: boolean; onChanged?: () => void }) {
  const [version, setVersion] = useState(0);
  const seat = useData(() => receptionApi.seat(domain, extension).catch(() => null), [domain, extension, version]);
  const [plan, setPlan] = useState<ChangeSetView | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  const s = seat.data;
  if (!s?.inStitch) return null;
  const first = name.split(/\s+/)[0] || name;

  const change = async (next: 'standard' | 'receptionist', inOperators?: boolean) => {
    setBusy(true);
    setNote(null);
    try {
      const r = await receptionApi.setSeat(domain, extension, next, inOperators);
      if (r.changeSet) setPlan(r.changeSet);
      else {
        setVersion((v) => v + 1);
        onChanged?.();
        setNote({ tone: 'ok', text: next === 'receptionist' ? `${first} is a receptionist now. Their Console is in their menu.` : `${first} is back to a standard seat.` });
      }
    } catch (err) {
      setNote({ tone: 'crit', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Seat">
      <div className="card-body section-stack">
        <div className="segmented" role="group" aria-label={`${name}’s seat`}>
          {(
            [
              ['standard', 'Standard'],
              ['receptionist', 'Receptionist']
            ] as const
          ).map(([k, label]) => (
            <button key={k} type="button" className={s.seat === k ? 'on' : ''} aria-pressed={s.seat === k} disabled={!writable || busy || s.seat === k} onClick={() => void change(k)}>
              {label}
            </button>
          ))}
        </div>
        <span className="muted">
          {s.seat === 'receptionist'
            ? `${first} has the Console: they answer calls and send them on to people, groups, park and voicemail, or ask someone by message while the caller holds.`
            : 'Their own phone, Messages and calls. A receptionist also gets the Console, for answering the main line and sending calls on.'}
        </span>
        {s.seat === 'receptionist' && (
          <label className="check">
            <input type="checkbox" checked={s.inOperators} disabled={!writable || busy} onChange={(e) => void change('receptionist', e.target.checked)} />
            <span>
              In the Operators group
              <small className="muted"> · rings when someone calls Operators. Every receptionist is in it unless you take them out.</small>
            </span>
          </label>
        )}
        {note && <Notice tone={note.tone}>{note.text}</Notice>}
      </div>
      {plan && (
        <ReviewDrawer
          open
          tenant={domain}
          items={[]}
          planned={plan}
          onUnstage={() => undefined}
          onClose={(applied) => {
            setPlan(null);
            setVersion((v) => v + 1);
            if (applied) onChanged?.();
          }}
        />
      )}
    </Card>
  );
}
